import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'
import Sheet from '@argo/common/components/Sheet'
import { api, money, PROFILE_FACTS, VEHICLE_FACTS, type Product, type Quote } from '../api'
import { useSession } from '../session'

/**
 * What can be bought. Choosing a product asks for a price; the price asks for
 * exactly the facts its tariff reads and nothing else; the holder confirms
 * those facts are true and buys at that price. A bond is priced the same way,
 * naming its landlord.
 */
export default function Shop() {
  const { t } = useTranslation()
  const products = useQuery({ queryKey: ['products'], queryFn: api.products })
  const [buying, setBuying] = useState<Product | null>(null)

  return (
    <div className="page page-mid stack">
      <div className="panel">
        <div className="panel-head"><span className="grow">{t('catalog')}</span></div>
        <div className="panel-body">
          {products.isLoading && <div className="skeleton" style={{ height: 90 }} />}
          {!products.isLoading && (products.data?.data.length ?? 0) === 0 && <p className="empty">{t('noProducts')}</p>}
          <div className="cards">
            {(products.data?.data ?? []).map(p => (
              <div className="card" key={p.code}>
                <div className="card-head">
                  <span className="chip">{p.kind === 'bond' ? t('bondWord') : t('coverWord')}</span>
                  <span className="title">{p.name}</span>
                </div>
                {p.description && <p className="field-hint">{p.description}</p>}
                {/* What it costs, where the product says so plainly: a buyer
                    should not have to open the sheet to learn the rate. */}
                {!!p.rate_bps && (
                  <p className="field-hint"><b>{t('rateLine', { rate: (Number(p.rate_bps) / 100).toFixed(2).replace(/\.?0+$/, '') })}</b></p>
                )}
                <div className="figures">
                  <div className="figure">
                    <span className="label">{p.kind === 'bond' ? t('bondAmount') : t('coverageLimit')}</span>
                    <span className="value num">{money(p.coverage_limit)}</span>
                  </div>
                  <div className="figure">
                    <span className="label">{t('term')}</span>
                    <span className="value">{t('days', { count: p.term_days ?? 0 })}</span>
                  </div>
                </div>
                <div className="row">
                  <span className="topbar-spacer" />
                  <button className="btn-small" onClick={() => setBuying(p)}>{t('getPrice')}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {buying && <BuySheet product={buying} onClose={() => setBuying(null)} />}
    </div>
  )
}

function BuySheet({ product, onClose }: { product: Product; onClose: () => void }) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { me } = useSession()
  const bond = product.kind === 'bond'

  const insured = useQuery({ queryKey: ['insured'], queryFn: api.insured })
  const [coverage, setCoverage] = useState(bond ? '' : String(product.coverage_limit ?? ''))
  const [landlord, setLandlord] = useState('')
  const [referral, setReferral] = useState('')
  const [vehicle, setVehicle] = useState('')
  const [choices, setChoices] = useState<Record<string, string>>({})
  const [licence, setLicence] = useState('')
  const [quote, setQuote] = useState<Quote | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [pin, setPin] = useState('')
  const [adding, setAdding] = useState(false)

  // Any change to what was priced makes the price stale: ask again.
  const changed = <T,>(set: (v: T) => void) => (v: T) => { set(v); if (quote?.complete) { setQuote(null); setConfirmed(false) } }

  const price = useMutation({
    mutationFn: async () => {
      if (licence && Number(licence) !== insured.data?.licence_year) {
        await api.setLicenceYear(Number(licence))
        qc.invalidateQueries({ queryKey: ['insured'] })
      }
      return api.quoteFor({
        product: product.code,
        coverage: coverage ? Number(coverage) : undefined,
        vehicle: vehicle || undefined,
        choices,
        referral: referral.trim() || undefined,
        beneficiary: bond ? landlord.trim() : undefined,
      })
    },
    onSuccess: q => {
      setQuote(q); setConfirmed(false)
      // The price is the answer the buyer asked for, and it renders below the
      // form that asked for it; bring it to them rather than making them hunt.
      if (q.complete) setTimeout(() => document.getElementById('premium')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60)
    },
    onError: (e: Error) => message.error(e.message),
  })

  const buy = useMutation({
    mutationFn: () => api.buyQuote({ quote: quote!.quote!, confirm: true, pin: pin || undefined }),
    onSuccess: () => {
      message.success(t('done'))
      for (const k of ['policies', 'me', 'insured']) qc.invalidateQueries({ queryKey: [k] })
      onClose()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const uses = quote?.uses ?? []
  const needs = quote?.needs ?? []
  const wantsVehicle = uses.some(f => VEHICLE_FACTS.includes(f))
  const choiceFacts = uses.filter(f => !VEHICLE_FACTS.includes(f) && !PROFILE_FACTS.includes(f))
  const label = (fact: string) => quote?.labels?.[fact] ?? t(fact, { defaultValue: fact })
  const fixedCover = !!quote?.fixed_cover
  const unknownProfile = needs.filter(f => f === 'age' || f === 'sex')
  const total = (quote?.premium ?? 0) + (bond ? Number(quote?.bond ?? 0) : 0)

  return (
    <Sheet open title={product.name} onClose={onClose}>
      <div className="stack" style={{ gap: 12 }}>
        {fixedCover ? (
          <div className="field">
            <span className="label">{t('fixedCover')}</span>
            <p className="num" id="cover-fixed">{money(product.coverage_limit)}</p>
            <p className="field-hint">{t('fixedCoverHint')} · {t('term')} {t('days', { count: product.term_days ?? 0 })}</p>
          </div>
        ) : (
          <div className="field">
            <label htmlFor="cover">{bond ? t('bondAmount') : t('coverAmount')}</label>
            <div className="input-wrap">
              <input id="cover" value={coverage} inputMode="numeric" autoComplete="off" placeholder="0"
                onChange={e => changed(setCoverage)(e.target.value.replace(/[^\d]/g, ''))} />
            </div>
            <p className="field-hint">{t('coverageLimit')} {money(product.coverage_limit)} · {t('term')} {t('days', { count: product.term_days ?? 0 })}</p>
          </div>
        )}

        {bond && (
          <div className="field">
            <label htmlFor="landlord">{t('landlordUsername')}</label>
            <div className="input-wrap">
              <input id="landlord" value={landlord} autoComplete="off" onChange={e => changed(setLandlord)(e.target.value)} />
            </div>
            {quote?.beneficiary && <p className="field-hint">{t('landlord')}: <b>{quote.beneficiary.name}</b></p>}
          </div>
        )}

        {wantsVehicle && (
          <div className="field">
            <label htmlFor="vehicle">{t('vehicle')}</label>
            <div className="row" style={{ gap: 8 }}>
              <div className="input-wrap" style={{ flex: 1 }}>
                <select id="vehicle" className="select" value={vehicle} onChange={e => changed(setVehicle)(e.target.value)}>
                  <option value="">{t('chooseVehicle')}</option>
                  {(insured.data?.vehicles ?? []).map(v => (
                    <option key={v.id} value={v.id}>{v.plate} · {v.vehicle_class}{v.make_model ? ` · ${v.make_model}` : ''}</option>
                  ))}
                </select>
              </div>
              <button className="btn-ghost" onClick={() => setAdding(a => !a)}>{t('addVehicle')}</button>
            </div>
            {adding && (
              <VehicleForm classes={quote?.options?.vehicle_class}
                onSaved={id => { setAdding(false); changed(setVehicle)(id) }} />
            )}
          </div>
        )}

        {(needs.includes('driving_years') || uses.includes('driving_years')) && (
          <div className="field">
            <label htmlFor="licence">{t('licenceYear')}</label>
            <div className="input-wrap">
              <input id="licence" inputMode="numeric" autoComplete="off"
                value={licence || String(insured.data?.licence_year ?? '')}
                onChange={e => changed(setLicence)(e.target.value.replace(/[^\d]/g, '').slice(0, 4))} />
            </div>
          </div>
        )}

        {choiceFacts.map(f => {
          // A tariff that lists a fact's values is asking the holder to pick
          // one; a tariff that does not — a plate, a register number — is
          // asking them to write it. The id is the same either way, so a
          // caller driving the form does not have to know which it is.
          const values = quote?.options?.[f] ?? []
          return (
            <div className="field" key={f}>
              <label htmlFor={`c-${f}`}>{label(f)}</label>
              <div className="input-wrap">
                {values.length > 0 ? (
                  <select id={`c-${f}`} className="select" value={choices[f] ?? ''}
                    onChange={e => changed(setChoices)({ ...choices, [f]: e.target.value })}>
                    <option value="" />
                    {values.map(o => <option key={o} value={o}>{t(o, { defaultValue: o })}</option>)}
                  </select>
                ) : (
                  <input id={`c-${f}`} value={choices[f] ?? ''} autoComplete="off"
                    onChange={e => changed(setChoices)({ ...choices, [f]: e.target.value })} />
                )}
              </div>
            </div>
          )
        })}

        <div className="field">
          <label htmlFor="referral">{t('referralCode')} <span className="muted">({t('optional')})</span></label>
          <div className="input-wrap">
            <input id="referral" value={referral} autoComplete="off" onChange={e => changed(setReferral)(e.target.value)} />
          </div>
          {quote?.referral && <p className="field-hint">{t('referredBy')}: <b>{quote.referral.name}</b></p>}
        </div>

        {unknownProfile.length > 0 && <p className="notice">{t('unknownRegNo')}</p>}
        {quote && !quote.complete && needs.length > 0 && unknownProfile.length === 0 && (
          <p className="notice">{t('needsTitle')}: {needs
            // Choosing a vehicle answers all of its facts at once: ask for it, not them.
            .filter(f => !(needs.includes('vehicle') && VEHICLE_FACTS.includes(f)))
            .map(f => f === 'vehicle' ? t('vehicle') : label(f)).join(', ')}</p>
        )}

        {!quote?.complete && (
          <button className="submit buy" disabled={price.isPending || (bond && (!coverage || !landlord.trim()))}
            onClick={() => price.mutate()}>
            {price.isPending ? t('pricing') : t('getPrice')}
          </button>
        )}

        {quote?.complete && (
          <>
            <div className="price-line">
              <span className="stack" style={{ gap: 2 }}>
                <span className="label">{bond ? t('youPayTotal') : t('youPay')}</span>
                <span className="sub">{t('balance')} {money(me?.balances?.STABLE)}</span>
              </span>
              <span className="value num" id="premium">{money(bond ? total : quote.premium)}</span>
            </div>
            {bond && (
              <dl className="review-grid">
                <dt>{t('bondAmount')}</dt><dd className="num">{money(quote.bond)}</dd>
                <dt>{t('fee')}</dt><dd className="num">{money(quote.premium)}</dd>
              </dl>
            )}

            {quote.external && (
              <div className="panel" id="external">
                <div className="panel-head"><span className="grow">{t('externalContract')}</span></div>
                <div className="panel-body">
                  <dl className="review-grid">
                    <dt>{t('externalSource')}</dt><dd>{quote.external.source.toUpperCase()}</dd>
                    {quote.external.policy_number && (
                      <><dt>{t('externalNumber')}</dt><dd className="mono">{quote.external.policy_number}</dd></>
                    )}
                    {quote.external.insuree_name && (
                      <><dt>{t('externalHolder')}</dt><dd>{quote.external.insuree_name}</dd></>
                    )}
                    {quote.external.vehicle && (
                      <><dt>{t('externalVehicle')}</dt><dd>{[quote.external.vehicle.plateNumber,
                        quote.external.vehicle.className, quote.external.vehicle.markName,
                        quote.external.vehicle.modelName, quote.external.vehicle.buildYear]
                        .filter(Boolean).join(' · ')}</dd></>
                    )}
                    {(quote.external.drivers?.length ?? 0) > 0 && (
                      <><dt>{t('externalDrivers')}</dt><dd>{quote.external.drivers!
                        .map(d => d.name || d.registerNumber || d.register_number).filter(Boolean).join(', ')}</dd></>
                    )}
                  </dl>
                  <p className="field-hint">{t('externalNotice')}</p>
                </div>
              </div>
            )}

            {(quote.breakdown?.length ?? 0) > 0 && (
              <details className="more">
                <summary>{t('howPriced')}</summary>
                <div className="panel-body">
                  <dl className="review-grid">
                    <dt>{t('baseAmount')}</dt><dd className="num">{money(quote.base)}</dd>
                    {quote.breakdown!.map(l => (
                      <span key={l.fact} style={{ display: 'contents' }}>
                        <dt>{l.name} · {t(l.value, { defaultValue: l.value })}</dt><dd className="num">× {l.factor}</dd>
                      </span>
                    ))}
                  </dl>
                </div>
              </details>
            )}

            {Object.keys(quote.facts).length > 0 && (
              <details className="more">
                <summary>{t('priceUsed')}</summary>
                <div className="panel-body">
                  <dl className="review-grid">
                    {Object.entries(quote.facts).map(([k, v]) => (
                      <span key={k} style={{ display: 'contents' }}>
                        <dt>{label(k)}</dt><dd>{t(String(v), { defaultValue: String(v) })}</dd>
                      </span>
                    ))}
                  </dl>
                  <div className="row" style={{ marginTop: 10 }}>
                    <span className="topbar-spacer" />
                    <Link className="btn-ghost" to="/profile" onClick={onClose}>{t('editDetails')}</Link>
                  </div>
                </div>
              </details>
            )}

            {bond
              ? <p className="notice">{t('bondNotice', { days: product.release_days })}</p>
              : <><p className="notice">{t('coverNotice')}</p>
                  <p className="notice">{product.refund === 'pro_rata' ? t('coverNoticeRefund') : t('coverNoticeNoRefund')}</p></>}
            {quote.expires_at && (
              <p className="field-hint">{t('quoteExpires', { time: new Date(quote.expires_at).toLocaleTimeString() })}</p>
            )}

            <label className="check">
              <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
              <span>{t('confirmFacts')}<br /><span className="muted">{t('falseInfo')}</span></span>
            </label>

            {me?.pin?.set && (
              <div className="field">
                <label htmlFor="buypin">{t('pin')}</label>
                <div className="input-wrap">
                  <input id="buypin" value={pin} onChange={e => setPin(e.target.value)} inputMode="numeric"
                    type="password" autoComplete="off" />
                </div>
              </div>
            )}

            <button className="submit buy" disabled={!confirmed || buy.isPending} onClick={() => buy.mutate()}>
              {buy.isPending ? t('buying') : bond ? t('placeBond') : t('buy')}
            </button>
          </>
        )}
      </div>
    </Sheet>
  )
}

/** A vehicle added where it is needed, without leaving the purchase. Shared with the profile. */
export function VehicleForm({ classes, onSaved }: { classes?: string[]; onSaved: (id: string) => void }) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [plate, setPlate] = useState('')
  const [cls, setCls] = useState('')
  const [model, setModel] = useState('')
  const [year, setYear] = useState('')
  const [value, setValue] = useState('')

  const save = useMutation({
    mutationFn: () => api.addVehicle({
      plate, vehicle_class: cls, make_model: model,
      year: year ? Number(year) : null, value: value || null,
    }),
    onSuccess: r => { qc.invalidateQueries({ queryKey: ['insured'] }); onSaved(String(r.added)) },
    onError: (e: Error) => message.error(e.message),
  })

  return (
    <div className="card" style={{ marginTop: 8 }}>
      <div className="figures">
        <div className="field">
          <label htmlFor="v-plate">{t('plate')}</label>
          <div className="input-wrap"><input id="v-plate" value={plate} onChange={e => setPlate(e.target.value.toUpperCase())} /></div>
        </div>
        <div className="field">
          <label htmlFor="v-class">{t('vehicleClass')}</label>
          <div className="input-wrap">
            {classes?.length
              ? <select id="v-class" className="select" value={cls} onChange={e => setCls(e.target.value)}>
                  <option value="" />
                  {classes.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              : <input id="v-class" value={cls} onChange={e => setCls(e.target.value)} />}
          </div>
        </div>
        <div className="field">
          <label htmlFor="v-model">{t('makeModel')}</label>
          <div className="input-wrap"><input id="v-model" value={model} onChange={e => setModel(e.target.value)} /></div>
        </div>
        <div className="field">
          <label htmlFor="v-year">{t('yearMade')}</label>
          <div className="input-wrap">
            <input id="v-year" inputMode="numeric" value={year} onChange={e => setYear(e.target.value.replace(/[^\d]/g, '').slice(0, 4))} />
          </div>
        </div>
        <div className="field figure wide">
          <label htmlFor="v-value">{t('vehicleValue')} <span className="muted">({t('optional')})</span></label>
          <div className="input-wrap">
            <input id="v-value" inputMode="numeric" value={value} onChange={e => setValue(e.target.value.replace(/[^\d]/g, ''))} />
          </div>
        </div>
      </div>
      <button className="btn-small" disabled={!plate || !cls || save.isPending} onClick={() => save.mutate()}>{t('save')}</button>
    </div>
  )
}
